/**
 * Help Centre's fixture — 1:20683.
 *
 * Placeholder for now: nothing serves this page yet, so the entries below match
 * the reference implementation rather than a response.
 *
 * NEEDS MANUAL CORRECTION when the section gets an API — most likely only the
 * rule figures (drawdown percentages, allocation caps) move, since those differ
 * per plan and should not be restated here once the account is known.
 *
 * Two kinds of destination, which is exactly what `INTRO` promises:
 *   `to`    an in-app route, for an answer whose subject is a page we own —
 *           being told where your credentials are and then having to find the
 *           page yourself is the wrong half of the job
 *   `href`  the public help centre, for anything that is an article rather
 *           than a screen. Opens in a new tab so the dashboard is not lost.
 * An entry with neither is plain text and does not pretend to be clickable.
 */

/** The help centre itself — its index lists every rule. */
export const HELP_CENTRE_URL = 'https://help.alphacapitalgroup.uk/en/';

/** Every article lives under the same host. */
const article = (slug) => `${HELP_CENTRE_URL}articles/${slug}`;

export const INTRO =
  'Rules, answers and support for every Alpha account. Articles open in the help ' +
  'centre; in-product links go to the matching dashboard page.';

export const TRADING_RULES = [
  {
    id: 'daily-drawdown',
    question: 'Max daily drawdown',
    answer:
      'Max daily drawdown resets at the daily candle open, 00:00 broker time. On ' +
      'Alpha Direct, Alpha One, Alpha Three and Alpha Pro 6%, it’s measured from ' +
      'the higher of the previous day’s closing balance or equity. On Alpha Pro 8%,' +
      ' Alpha Pro 10%, Swing and Free Trial, it’s measured from the previous day’s ' +
      'closing balance. The limits are:\n' +
      '3% on Alpha Direct, Alpha Pro 6% and Alpha One 6% (3% is the evaluation ' +
      'limit on Alpha One 6%)\n' +
      '4% on Alpha Pro 8%, Alpha One 10% and Alpha Three\n' +
      '5% on Alpha Pro 10%, Alpha One 12%, Swing and Free Trial\n' +
      'Breaches are applied on current equity, so open losses count.',
    href: article('6934210-what-are-the-daily-risk-limits-and-how-do-they-work'),
  },
  {
    id: 'max-drawdown',
    question: 'Max drawdown',
    answer:
      'Max drawdown is static or trailing depending on the plan. Static max ' +
      'drawdown is measured from the starting balance: 6% on Alpha Pro 6% and Alpha' +
      ' Three, 8% on Alpha Pro 8%, and 10% on Alpha Pro 10%, Swing and Free Trial. ' +
      'Trailing max drawdown follows your highest closed balance: 4% on Alpha One ' +
      '6%, 6% on Alpha One 10%, 8% on Alpha One 12%, and 5% on Alpha Direct. It ' +
      'stops rising once it reaches your starting balance. The account is breached ' +
      'if either equity or balance falls below the limit.',
    href: article('6934220-what-is-the-maximum-total-loss'),
  },
  {
    id: 'news',
    question: 'News trading',
    answer:
      'News trading is unrestricted during evaluation. On a Qualified Analyst ' +
      'account on Pro, One, Three or Direct, there is a 10-minute window, 5 minutes' +
      ' either side of the release. During it you may neither open nor close a ' +
      'trade on the targeted instrument. On Swing the window is 4 minutes, and the ' +
      'trade must last longer than 2 minutes.',
    href: article('9293522-can-i-trade-news'),
  },
  {
    id: 'max-risk',
    question: 'Max Risk Rule',
    answer:
      'On a Qualified Analyst account, the account is closed if open drawdown on ' +
      'any single asset reaches the maximum risk threshold for your account size. ' +
      'The threshold is 3% on accounts below 50K and 2% on accounts of 50K and ' +
      'above. Alpha Direct accounts have a 1% threshold at every size, and the rule' +
      ' applies from the first trade.',
    href: article('15888623-what-is-the-max-risk-rule-and-how-does-it-work'),
  },
  {
    id: 'allocation',
    question: 'Allocation caps',
    answer:
      '$400,000 per household and $300,000 per strategy on Qualified Analyst ' +
      'accounts, where strategy means the traded asset. No cap on evaluation accounts.',
    href: article(
      '8804535-how-many-accounts-can-i-have-what-is-the-maximum-allocation-per-strategy',
    ),
  },
];

export const FAQS = [
  {
    id: 'credentials',
    question: 'Where are my credentials?',
    answer:
      'Account Metrics, in the Account information block, for every account you hold.',
    to: '/account-metrics',
  },
  {
    id: 'kyc',
    question: 'Identity verification',
    answer:
      'Required before a payout can be requested. Complete the identity check ' +
      'under Verification → KYC Verification.',
    to: '/verification/identity',
  },
  {
    id: 'invoice',
    question: 'Where is my invoice?',
    answer: 'Payment History. Each purchase keeps its own receipt.',
    to: '/payment-history',
  },
  {
    id: 'wait',
    question: 'How long until I get my credentials?',
    answer: 'Immediately on card. Up to 24 hours on crypto and PayPal.',
    href: article('6933849-how-long-will-it-take-to-get-my-credentials'),
  },
  {
    id: 'platforms',
    question: 'Which platforms can I use?',
    answer:
      'MetaTrader 5, cTrader, DX Trade and TradeLocker. MetaTrader 5 is not ' +
      'available to US residents. cTrader is closed to new US registrations.',
    href: article('6933883-what-trading-platforms-are-available-for-use'),
  },
  {
    id: 'payout',
    question: 'When can I request a payout?',
    answer:
      'Bi-weekly or on demand, depending on the plan. A Qualified Analyst account ' +
      'needs a minimum of 2% gross profit, and identity verification, before a request.',
    href: article('6933755-how-do-i-get-paid-performance-fees'),
  },
];

/** The two ways to reach a person, when the answers above did not do it. */
export const CHANNELS = [
  {
    id: 'discord',
    label: 'Discord',
    note: 'discord.com/invite/W5aKKmPRdR, slower at weekends',
    logo: '/images/channel-discord.svg',
    action: 'Join',
    href: 'https://discord.com/invite/W5aKKmPRdR',
  },
  {
    id: 'email',
    label: 'Email',
    note: 'info@alphacapitalgroup.uk, weekdays 08:00 to 20:00 GMT',
    logo: '/images/channel-email.svg',
    action: 'Write',
    href: 'mailto:info@alphacapitalgroup.uk',
  },
];
